import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useSidebar } from './SidebarContext';
import supabase from '../utils/supabase';

// ── Types ──────────────────────────────────────────────────────────────────

interface ComplianceUser {
  id: string;
  email: string;
  name?: string;
}

interface ComplianceContextValue {
  isCompliance: boolean;
  complianceUser: ComplianceUser | null;
  complianceToken: string | null;
  loading: boolean;
  logout: () => void;
}

const noop = () => {};

const ComplianceContext = createContext<ComplianceContextValue>({
  isCompliance: false,
  complianceUser: null,
  complianceToken: null,
  loading: true,
  logout: noop,
});

export const useCompliance = () => useContext(ComplianceContext);

const COMPLIANCE_SIDEBAR_ITEMS = [
  { id: 'compliance-agencies', label: 'Agencies', to: '/compliance', icon: '🏢' },
];

export const ComplianceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [complianceUser, setComplianceUser] = useState<ComplianceUser | null>(null);
  const [complianceToken, setComplianceToken] = useState<string | null>(() => sessionStorage.getItem('complianceToken'));
  const [loading, setLoading] = useState(true);

  const { setItems, setFooterItems, setActiveItem } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const clearSidebar = useCallback(() => {
    setItems([]);
    setFooterItems([]);
    setActiveItem(null);
  }, [setItems, setFooterItems, setActiveItem]);

  const doLogout = useCallback(() => {
    sessionStorage.removeItem('complianceToken');
    setComplianceToken(null);
    setComplianceUser(null);
    clearSidebar();
    supabase.auth.signOut();
    navigate('/compliance/login');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSidebar, navigate]);

  const setupSidebar = useCallback((pathname: string) => {
    setItems(COMPLIANCE_SIDEBAR_ITEMS as any);
    setFooterItems([{ id: 'compliance-logout', label: 'Logout', onClick: () => doLogout() }] as any);
    const active = COMPLIANCE_SIDEBAR_ITEMS.find(
      item => pathname === item.to || (item.to !== '/compliance' && pathname.startsWith(item.to))
    );
    setActiveItem(active ? active.id : 'compliance-agencies');
  }, [setItems, setFooterItems, setActiveItem, doLogout]);

  // Verify token once on mount
  useEffect(() => {
    const token = sessionStorage.getItem('complianceToken');
    if (!token) { setLoading(false); return; }
    let mounted = true;
    axios
      .get(`${API_BASE_URL}/api/compliance/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (mounted) { setComplianceToken(token); setComplianceUser(res.data.user); } })
      .catch(() => { if (mounted) { sessionStorage.removeItem('complianceToken'); setComplianceToken(null); } })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply sidebar on every compliance page navigation
  useEffect(() => {
    if (!complianceUser) return;
    if (location.pathname.startsWith('/compliance') && location.pathname !== '/compliance/login') {
      setupSidebar(location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, complianceUser, setupSidebar]);

  return (
    <ComplianceContext.Provider
      value={{
        isCompliance: Boolean(complianceToken && complianceUser),
        complianceUser,
        complianceToken,
        loading,
        logout: doLogout,
      }}
    >
      {children}
    </ComplianceContext.Provider>
  );
};
